import { DOCUMENT, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { RouteTitleStrategy } from './page-title';
import { ROUTE_LASTMOD, SITE_LASTMOD } from './route-lastmod.generated';
import { createBreadcrumbList, type BreadcrumbItem } from './seo-breadcrumbs';
import { PageSeoData } from './seo-metadata';
import { BASE_URL } from './sitemap-pages';

/**
 * Per-route dateModified for the WebPage JSON-LD. Falls back to the
 * site-wide max if the URL isn't in the lookup (e.g. unknown query
 * params or routes added since `nx run russrodgersbassguitar:generate-sitemap`
 * last ran).
 * Strips any trailing query / fragment so /blog/foo?utm=... matches
 * the /blog/foo entry.
 */
function lookupRouteLastmod(url: string): string {
  const path = url.split('?')[0]?.split('#')[0] ?? url;
  return ROUTE_LASTMOD[path] ?? SITE_LASTMOD;
}

const DEFAULT_DESCRIPTION =
  'Russ Rodgers offers personalized bass guitar lessons online via Zoom. Over 40 years of teaching and performance experience.';

@Injectable({
  providedIn: 'root',
})
export class SEOService {
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private meta = inject(Meta);
  private routeTitleStrategy = inject(RouteTitleStrategy);
  private organizationSchema: Record<string, unknown> = {
    '@type': 'EducationalOrganization',
    '@id': `${BASE_URL}/#organization`,
    url: BASE_URL,
    sameAs: [
      'https://www.facebook.com/russrodgersbassguitar/',
      'https://www.instagram.com/russbassrodgers/',
      'https://www.youtube.com/@RussellRodgers',
      'https://www.linkedin.com/in/russ-rodgers-3724904/',
    ],
    foundingDate: '1985',
    logo: `${BASE_URL}/images/russlogo-200x200-black-bg.webp`,
    name: 'Russ Rodgers Bass Guitar Instruction',
    description: DEFAULT_DESCRIPTION,
    email: 'russrodgers@gmail.com',
    telephone: '+1-404-386-0983',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-404-386-0983',
      email: 'russrodgers@gmail.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
    dateModified: SITE_LASTMOD,
  };
  // NOTE: no site-wide Person node. A standalone Person was tried before and
  // Google dropped it in favour of the site-wide Organization (see the
  // "(Person removed)" bio test). The instructor signal lives instead as an
  // inline Person on the Course (online-bass-lessons route) — page-specific and
  // semantically anchored to "who teaches the course" rather than re-adding a
  // dropped graph node on every page.

  /**
   * Runtime override for per-page schemas, description, title, and/or robots
   * meta. Set by components that need dynamic SEO content on a route param
   * change (e.g. BlogPostComponent emitting BlogPosting for the current :slug,
   * or hub components emitting noindex for thin pages). Cleared automatically
   * when the URL changes so overrides never leak to a different page.
   */
  private runtimeOverride: {
    url: string;
    schemas?: Record<string, unknown>[];
    description?: string;
    title?: string;
    robots?: string;
  } | null = null;

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.emitForUrl(event.urlAfterRedirects);
      });
  }

  /**
   * Override the per-route JSON-LD schemas for the current URL. Triggers an
   * immediate re-emit of the @graph so the DOM reflects the new schemas before
   * the next change-detection cycle. The override is discarded automatically
   * when the URL changes.
   */
  setRouteSchemas(schemas: Record<string, unknown>[]): void {
    const url = this.router.url;
    const existing =
      this.runtimeOverride?.url === url ? this.runtimeOverride : {};
    this.runtimeOverride = { ...existing, url, schemas };
    this.emitForUrl(url);
  }

  /**
   * Override the per-route description for the current URL. Used alongside
   * setRouteSchemas for per-post meta descriptions.
   */
  setRouteDescription(description: string): void {
    const url = this.router.url;
    const existing =
      this.runtimeOverride?.url === url ? this.runtimeOverride : {};
    this.runtimeOverride = { ...existing, url, description };
    this.emitForUrl(url);
  }

  /**
   * Override the per-route title for the current URL. Mirrors the document
   * <title> into og:title, twitter:title, and the WebPage JSON-LD name.
   * Pass the fully-suffixed string (i.e. include `${BRAND_SUFFIX}`).
   */
  setRouteTitle(title: string): void {
    const url = this.router.url;
    const existing =
      this.runtimeOverride?.url === url ? this.runtimeOverride : {};
    this.runtimeOverride = { ...existing, url, title };
    this.emitForUrl(url);
  }

  /** Clear any runtime override and re-emit using the current route's static SEO data. */
  clearRouteSchemas(): void {
    this.runtimeOverride = null;
    this.emitForUrl(this.router.url);
  }

  /**
   * Emit `noindex, follow` for the current URL. Used by hub components when
   * their post count is below HUB_INDEX_MIN_POSTS (thin pages). Cleared
   * automatically when the URL changes so the tag never leaks to the next
   * route.
   */
  setRobotsNoindex(): void {
    const url = this.router.url;
    const existing =
      this.runtimeOverride?.url === url ? this.runtimeOverride : {};
    this.runtimeOverride = { ...existing, url, robots: 'noindex, follow' };
    this.emitForUrl(url);
  }

  /**
   * Remove any robots meta override for the current URL. Restores default
   * indexability (no robots meta = engines index normally). All other
   * override fields (title, description, schemas) are preserved.
   */
  clearRobots(): void {
    if (this.runtimeOverride) {
      const { url, schemas, description, title } = this.runtimeOverride;
      const rebuild: typeof this.runtimeOverride = { url };
      if (schemas !== undefined) rebuild.schemas = schemas;
      if (description !== undefined) rebuild.description = description;
      if (title !== undefined) rebuild.title = title;
      this.runtimeOverride = rebuild;
    }
    this.emitForUrl(this.router.url);
  }

  private emitForUrl(url: string): void {
    if (this.runtimeOverride && this.runtimeOverride.url !== url) {
      this.runtimeOverride = null;
    }
    const seoData = this.computeEffectiveSeoData();
    const title =
      this.runtimeOverride?.title ??
      this.routeTitleStrategy.getTitle(this.router.routerState.snapshot);
    this.updateData(url, title, seoData);
    this.updateJsonLD(url, title, seoData);
  }

  private computeEffectiveSeoData(): PageSeoData | undefined {
    const routeData = this.getRouteSeoData();
    if (!this.runtimeOverride) return routeData;
    const description =
      this.runtimeOverride.description ??
      routeData?.description ??
      DEFAULT_DESCRIPTION;
    const jsonLd = this.runtimeOverride.schemas ?? routeData?.jsonLd;
    return jsonLd !== undefined ? { description, jsonLd } : { description };
  }

  private getRouteSeoData(): PageSeoData | undefined {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let seoData: PageSeoData | undefined;
    while (route) {
      const data = route.data['seo'] as PageSeoData | undefined;
      if (data) {
        seoData = data;
      }
      route = route.firstChild;
    }
    return seoData;
  }

  /**
   * Walks the deepest active route snapshot for `data.breadcrumbs`. A
   * runtime override (`setRouteSchemas`) replaces only `pageSchemas` — it
   * never touches this — so dynamic routes (e.g. blog posts) that emit
   * their own BreadcrumbList via `buildBlogPostSchemas` don't also get one
   * from route data (their route declares no `data.breadcrumbs`).
   *
   * BreadcrumbList always precedes the route's own `jsonLd` in the emitted
   * `@graph` (see `updateJsonLD` below: `[...breadcrumbSchemas,
   * ...pageSchemas]`) — this is the contract, not an incidental ordering.
   * `e2e/data/pages.ts` rows list their `seo.schemas` in this same order
   * (`BreadcrumbList` first) for `e2e/seo-validation.spec.ts`'s positional
   * `@graph` assertion.
   */
  private getRouteBreadcrumbs(): BreadcrumbItem[] | undefined {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let crumbs: BreadcrumbItem[] | undefined;
    while (route) {
      const data = route.data['breadcrumbs'] as BreadcrumbItem[] | undefined;
      if (data) crumbs = data;
      route = route.firstChild;
    }
    return crumbs;
  }

  private updateJsonLD(
    url: string,
    title: string,
    seoData: PageSeoData | undefined,
  ): void {
    const pageUrl = `${BASE_URL}${url}`;
    const description = seoData?.description ?? DEFAULT_DESCRIPTION;

    const webPageSchema: Record<string, unknown> = {
      '@type': 'WebPage',
      name: title,
      description,
      url: pageUrl,
      inLanguage: 'en-US',
      dateModified: lookupRouteLastmod(url),
      isPartOf: {
        '@type': 'WebSite',
        url: BASE_URL,
        name: 'Russ Rodgers Bass Guitar Instruction',
      },
    };
    const pageSchemas: Record<string, unknown>[] = seoData?.jsonLd
      ? Array.isArray(seoData.jsonLd)
        ? seoData.jsonLd
        : [seoData.jsonLd]
      : [];
    const crumbs = this.getRouteBreadcrumbs();
    const breadcrumbSchemas = crumbs ? [createBreadcrumbList(crumbs)] : [];

    const graphData = {
      '@context': 'https://schema.org',
      '@graph': [
        this.organizationSchema,
        webPageSchema,
        ...breadcrumbSchemas,
        ...pageSchemas,
      ],
    };

    let scriptElement = this.document.getElementById('json-ld');
    if (!scriptElement) {
      scriptElement = this.document.createElement('script');
      scriptElement.id = 'json-ld';
      scriptElement.setAttribute('type', 'application/ld+json');
      this.document.head.appendChild(scriptElement);
    }
    scriptElement.textContent = JSON.stringify(graphData);
  }

  private updateData(
    url: string,
    title: string,
    seoData: PageSeoData | undefined,
  ): void {
    const pageUrl = `${BASE_URL}${url}`;
    const description = seoData?.description ?? DEFAULT_DESCRIPTION;

    let canonicalLink = this.document.head.querySelector('[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', pageUrl);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });

    // Robots meta: present only for noindex pages; removed on all others so
    // the tag never leaks from a thin hub to a subsequent page.
    const robots = this.runtimeOverride?.robots;
    if (robots !== undefined) {
      this.meta.updateTag({ name: 'robots', content: robots });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }
}
